import { Card, CardMedia, CardContent, Typography, Box, Chip, IconButton, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { useNavigate } from 'react-router-dom';
import type { Video } from '../../types';

interface VideoCardProps {
  video: Video;
  onMarkWatched?: (videoId: string) => void;
  onAddToWatchlist?: (videoId: string) => void;
  showWatchlistButton?: boolean;
  onSelectVideo?: (video: Video) => void;
}

function parseDuration(duration: string): string {
  const match = duration && duration?.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return '0:00';

  const hours = parseInt(match[1] || '0');
  const minutes = parseInt(match[2] || '0');
  const seconds = parseInt(match[3] || '0');

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatPublishedDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) return `Today ${time}`;
  if (diffDays === 1) return `Yesterday ${time}`;
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) + ` ${time}`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: diffDays >= 365 ? 'numeric' : undefined });
}

export default function VideoCard({
  video,
  onMarkWatched,
  onAddToWatchlist,
  showWatchlistButton = true,
  onSelectVideo,
}: VideoCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onSelectVideo) {
      onSelectVideo(video);
      return;
    }
    try {
      const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?(): void };
      if (el.requestFullscreen) {
        el.requestFullscreen().catch(() => {});
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
    } catch {
      // iOS Safari throws when requestFullscreen is called on non-video elements
    }
    navigate(`/watch/${video.id}`);
  };

  const handleMarkWatched = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkWatched?.(video.id);
  };

  const handleAddToWatchlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToWatchlist?.(video.id);
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
      onClick={handleClick}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="180"
          image={video.thumbnail}
          alt={video.title}
          sx={{ objectFit: 'cover' }}
        />
        <Chip
          label={parseDuration(video.duration)}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
          }}
        />
      </Box>

      <CardContent>
        <Typography
          variant="subtitle1"
          component="h3"
          gutterBottom
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '3em',
          }}
        >
          {video.title}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {video.channelTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatPublishedDate(video.publishedAt)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {video.channelRating && (
              <Chip
                label={`⭐ ${video.channelRating}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {showWatchlistButton && (
              <Tooltip title="Add to watchlist">
                <IconButton
                  size="small"
                  onClick={handleAddToWatchlist}
                  sx={{ color: 'primary.main' }}
                >
                  <BookmarkAddIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Mark as watched">
              <IconButton
                size="small"
                onClick={handleMarkWatched}
                sx={{ color: 'success.main' }}
              >
                <CheckCircleIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
